import React, { Component, useEffect } from 'react';
import PropTypes from 'prop-types';
import Grid from '@mui/material/Grid';
import Box from '@mui/material/Box';
import SaveIcon from '@mui/icons-material/Save';
import ListAltIcon from '@mui/icons-material/ListAlt';
import Button from '@mui/material/Button';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';

import PopupAlert from '../../components/Notifications/PopupAlert';
import SearchItem from '../../components/TableMixin/SearchItem';
import DigitalClock from '../Custom/DigitalClock';
import DevicesSelection from './DevicesSelection';
import AssamblyLayout from './TabsTables/AssamblyLayout';
import CommandsLayout from './TabsTables/CommandsLayout';
import ConfigsTable from './TabsTables/ConfigsTable';
import StatusesTable from './TabsTables/StatusesTable';
import ConfigurationLogsModal from './ConfigurationLogsModal';
import { getUIinfo } from '../../containers/UIhelper/UIhelper';
import ExcelJS from 'exceljs';
import { getDateForExcelExports } from '../../actions/Utilities/dateUtils';
import {
  buildG5ConfigItems,
  G5_CONFIG_EXPORT_COLUMNS,
  getG5ConfigExportValue,
} from './TabsTables/g5ConfigColumns';

// redux
import { connect } from 'react-redux';
import _ from 'lodash';
import * as actionsDevices from '../../actions/DeviceConfigurationAction';
import * as actionsSensors from '../../actions/SensorsActions';
import * as actionsFiles from '../../actions/FilesActions';
import * as devicesActions from '../../actions/DeviceConfigurationAction';
import * as actionsProjects from '../../actions/ProjectsActions';
import * as actionsSearch from '../../actions/SearchActions';
import { downloadFileFromURl } from '../../actions/selectors';
import { doSignOut } from '../../actions/LoginActions';

class G5DevicesTabsView extends Component {
  constructor(props) {
    super(props);

    this.state = {
      mode: 'status',
      popupAlert: {
        popupKey: -1,
        message: '',
        severity: 'info',
        action: '',
        logsModalOpen: false,
      },
      xlsDevicesData: null,
      uiInfo: getUIinfo({ minWidth: 1400 }),
      activeTab: 0,
      deviceTypeFilter: { showEdge: true, showUltra: true },
    };
  }

  componentDidMount() {
    window.addEventListener('resize', this.updateUIinfo);
    // Ensure device list loaded if coming straight into modal
    if (this.props.deviceList.length === 0) {
      this.props.fetchDevices();
    } else {
      // Apply initial filter to existing devices
      const filteredDevices = this.filterDevicesByType(this.props.devicesForDisplay);
      const devicesDataSet = this.setDevicesData(filteredDevices);
      this.setState({ xlsDevicesData: devicesDataSet });
    }
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.updateUIinfo);
  }

  componentWillReceiveProps(nextProps) {
    if (!_.isEqual(this.props.deviceList, nextProps.deviceList)) {
      const filteredDevices = this.filterDevicesByType(nextProps.deviceList);
      const devicesDataSet = this.setDevicesData(filteredDevices);
      this.setState({ xlsDevicesData: devicesDataSet });
    }

    if (!_.isEqual(this.props.devicesForDisplay, nextProps.devicesForDisplay)) {
      const filteredDevices = this.filterDevicesByType(nextProps.devicesForDisplay);
      const devicesDataSet = this.setDevicesData(filteredDevices);
      this.setState({ xlsDevicesData: devicesDataSet });
    }
  }

  filterDevicesByType = (devices) => {
    const { showEdge, showUltra } = this.state.deviceTypeFilter;
    
    // If both unchecked, no devices should be included
    if (!showEdge && !showUltra) return [];
    
    // If both checked, all devices are included
    if (showEdge && showUltra) return devices;
    
    // Otherwise filter based on device type
    return devices.filter(device => {
      if (showEdge && device.isMini === 0) return true;
      if (showUltra && device.isMini === 1) return true;
      return false;
    });
  }

  updateUIinfo = () => {
    this.setState({ uiInfo: getUIinfo({ minWidth: 1400 }) });
  };

  // Helpers reused from MainPage (kept minimal to support Excel export)
  setDeviceDataTitle = (title, widthType, widthValue) => ({
    title,
    width: { [widthType || 'wch']: widthValue || title.length + 2 },
    style: { alignment: { horizontal: 'center', vertical: 'center' } },
  });

  setDevicesData = (devices) => {
    const configItems = buildG5ConfigItems(devices, this.props.options);
    const columns = G5_CONFIG_EXPORT_COLUMNS.map((column) =>
      this.setDeviceDataTitle(column.title, 'wch', Math.max(column.width / 8, 12))
    );

    const xlsData = [
      {
        columns: columns,
        data: configItems.map((device) =>
          G5_CONFIG_EXPORT_COLUMNS.map((column) => ({
            value: getG5ConfigExportValue(column, device),
            style: { font: { sz: '12' } },
          }))
        ),
      },
    ];

    return xlsData;
  };

  notify = (message, level, action) => {
    this.setState({
      popupAlert: {
        popupKey: new Date().getTime(),
        message,
        severity: level,
        action,
      },
    });
  };

  handleExportExcel = async () => {
    if (!this.state.xlsDevicesData) {
      this.notify('No data available for export', 'info');
      return;
    }
    const { projectsList, selectedProject } = this.props;
    const selectedProjectIndex = projectsList.projectIndexMap[selectedProject];
    const selectedProjectName =
      projectsList.items[selectedProjectIndex]?.ShortName;
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet('Devices');

    const dataSet = this.state.xlsDevicesData[0];
    worksheet.columns = dataSet.columns.map((col) => ({
      header: col.title,
      key: col.title,
      width: col.width.wch || col.width.wpx || col.title.length + 2,
    }));

    dataSet.data.forEach((row) => {
      const rowValues = row.map((cell) => cell.value);
      worksheet.addRow(rowValues);
    });

    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], { type: 'application/octet-stream' });
    const projectPrefix = selectedProjectName ? `${selectedProjectName}_` : '';
    const filename = `${projectPrefix}${getDateForExcelExports()}_G5_Devices.xlsx`;
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.style.display = 'none';
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
  };

  handleDeviceFilterChange = (filterState) => {
    this.setState({ deviceTypeFilter: filterState }, () => {
      // Update Excel data with the new filter
      const filteredDevices = this.filterDevicesByType(this.props.devicesForDisplay);
      const devicesDataSet = this.setDevicesData(filteredDevices);
      this.setState({ xlsDevicesData: devicesDataSet });
    });
  }

  render() {
    const {
      user,
      selectedProject,
      timeZone,
      deviceList,
      sort,
      devicesForDisplay,
      bitsInfo,
      selection,
      projectsList,
      fwVersions,
      fwUltraVersions,
      fwUltraModemVersions,
      fwModemVersions,
    } = this.props;

    // Filter the selection based on device type filter
    const filteredSelection = selection.filter(deviceId => {
      const device = deviceList.find(d => d.DeviceID === deviceId);
      if (!device) return false;
      
      const { showEdge, showUltra } = this.state.deviceTypeFilter;
      
      // If both unchecked, no devices should be selected
      if (!showEdge && !showUltra) return false;
      
      // If both checked, all selected devices are valid
      if (showEdge && showUltra) return true;
      
      // Otherwise check device type
      if (showEdge && device.isMini === 0) return true;
      if (showUltra && device.isMini === 1) return true;
      
      return false;
    });

    // Filter devicesForDisplay based on device type filter
    const filteredDevicesForDisplay = devicesForDisplay.filter(device => {
      const { showEdge, showUltra } = this.state.deviceTypeFilter;
      
      // If both unchecked, no devices should be displayed
      if (!showEdge && !showUltra) return false;
      
      // If both checked, all devices are displayed
      if (showEdge && showUltra) return true;
      
      // Otherwise check device type
      if (showEdge && device.isMini === 0) return true;
      if (showUltra && device.isMini === 1) return true;
      
      return false;
    });

    // Filter the full device list for DevicesSelection (same logic)
    const filteredDeviceList = deviceList.filter(device => {
      const { showEdge, showUltra } = this.state.deviceTypeFilter;
      
      // If both unchecked, no devices should be displayed
      if (!showEdge && !showUltra) return false;
      
      // If both checked, all devices are displayed
      if (showEdge && showUltra) return true;
      
      // Otherwise check device type
      if (showEdge && device.isMini === 0) return true;
      if (showUltra && device.isMini === 1) return true;
      
      return false;
    });


    const tabs = [
      {
        label: this.context.t('status'),
        style: { maxHeight: 'inherit', margin: 'auto', width: 'fit-content' },
        component: (
          <StatusesTable
            items={filteredDevicesForDisplay}
            sort={sort}
            selectedItems={filteredSelection}
            workingMode={this.state.mode}
            handleSelection={this.props.selectDevice}
            reloadDevices={this.props.fetchDevices}
            local={timeZone}
            options={this.props.options}
            uiInfo={this.state.uiInfo}
          />
        ),
      },
      {
        label: this.context.t('configuration'),
        style: { maxHeight: 'inherit', margin: 'auto', width: 'fit-content' },
        component: (
          <ConfigsTable
            items={filteredDevicesForDisplay}
            sort={sort}
            selectedItems={filteredSelection}
            workingMode={this.state.mode}
            handleSelection={this.props.selectDevice}
            reloadDevices={this.props.fetchDevices}
            local={timeZone}
            options={this.props.options}
            uiInfo={this.state.uiInfo}
          />
        ),
      },
    ];

    if (JSON.parse(process.env.REACT_APP_SHOW_G5_ASSEMBLY || false)) {
      tabs.push({
        label: this.context.t('assembly'),
        style: { maxHeight: 'inherit', margin: 'auto', width: 'fit-content' },
        component: (
          <AssamblyLayout
            bitsInfo={deviceList}
            timeZone={timeZone}
            selectMultipleDevices={this.props.selectMultipleDevices}
            updateConfig={this.props.updateConfig}
            reloadDevices={this.props.fetchDevices}
            notify={this.notify}
            selectedItems={this.props.selection}
          />
        ),
      });
    }

    if (JSON.parse(process.env.REACT_APP_SHOW_G5_COMMANDS || false)) {
      tabs.push({
        label: this.context.t('commands'),
        style: {
          maxHeight: 'inherit',
          width: 'fit-content',
          height: '84vh',
          overflowY: 'auto',
        },
        component: (
          <CommandsLayout
            hideClock
            getDefault={this.props.fetchProjectDefaultConfiguration}
            updateConfig={this.props.updateConfig}
            updateDefaultConfig={this.props.updateDefaultConfig}
            notify={this.notify}
            selectedItems={filteredSelection}
            sendActionCommand={this.props.sendActionCommand}
            timeZone={this.props.timeZone}
            enable={filteredSelection.length > 0}
            fwVersions={fwVersions}
            fwUltraVersions={fwUltraVersions}
            fwUltraModemVersions={fwUltraModemVersions}
            fwModemVersions={fwModemVersions}
            fm2ConfigOptions={this.props.options.FM2ConfigType}
            user={user}
            selectedProject={selectedProject}
            deviceTypeFilter={this.state.deviceTypeFilter}
          />
        ),
      });
    }

    return (
      <main>
        <Grid container direction="row" style={{ flexWrap: 'nowrap' }}>
          <Grid item style={{ width: '14vw' }}>
            <DevicesSelection
              devicesList={filteredDeviceList}
              selectedDevices={filteredSelection}
              handleSelection={this.props.selectDevice}
              onFilterChange={this.handleDeviceFilterChange}
              filterState={this.state.deviceTypeFilter}
            />
          </Grid>
          <Grid item style={{ width: '86vw', position: 'relative' }}>
            {/* Unified sticky toolbar */}
            <Box className="g5-devices-tabs-toolbar">
              {/* Search */}
              <SearchItem
                onSearchItem={(value, type) => {
                  this.props.searchDevice(value, type).then((messageData) => {
                    if (messageData) {
                      let message;
                      let action;
                      const { projectID, projectName } = messageData;
                      if (projectID == null) {
                        message = this.context.t('not_found');
                      } else {
                        if (projectID === selectedProject) {
                          message = this.context.t('remove_filters_to_view_device');
                        } else {
                          message = this.context.t('your_device_located_in_project_projectName', { projectName });
                          action = {
                            label: this.context.t('switch_to_projectName', { projectName }),
                            callback: () => {
                              this.props.handleProjectChange(projectID);
                            },
                          };
                        }
                      }
                      this.notify(message, 'info', action);
                    }
                  });
                }}
                path="g5Devices"
                user={user}
              />

              {/* Center area: clock (optional) + tabs + right buttons */}
              <Box sx={{ flexGrow: 0, mx: 'auto', display: 'flex', alignItems: 'center', overflow: 'hidden' }}>
                {this.state.activeTab === 2 && (
                  <Box sx={{ display: 'flex', alignItems: 'center', mr: 2 }}>
                    <span style={{ fontWeight: 'bold', marginRight: '6px' }}>{this.context.t('utc_time')}</span>
                    <DigitalClock />
                  </Box>
                )}

                <Tabs
                  value={this.state.activeTab}
                  onChange={(_event, newValue) => this.setState({ activeTab: newValue })}
                  aria-label="device tabs"
                  sx={{ }}
                >
                  {tabs.map((tab, index) => (
                    <Tab
                      key={index}
                      label={tab.label}
                      sx={{
                        fontSize: '1.3rem',
                        fontWeight: 600,
                        textTransform: 'none',
                        px: 1.5,
                        color: 'text.secondary',
                        '&.Mui-selected': {
                          color: 'primary.main',
                          fontWeight: 700,
                        },
                      }}
                    />
                  ))}
                </Tabs>

                {/* Right buttons */}
                <Box sx={{ ml: 60, display: 'flex', alignItems: 'center', flexShrink: 0 }}>
                  <Button
                    variant="contained"
                    color="primary"
                    size="small"
                    startIcon={<SaveIcon />}
                    onClick={this.handleExportExcel}
                    sx={{ mr: 1, whiteSpace: 'nowrap' }}
                  >
                    {this.context.t('save_as_excel')}
                  </Button>
                  {user.isAQS && (
                    <Button
                      variant="contained"
                      color="primary"
                      size="small"
                      startIcon={<ListAltIcon />}
                      sx={{ whiteSpace: 'nowrap' }}
                      onClick={() => this.setState({ logsModalOpen: true })}
                    >
                      {this.context.t('logs')}
                    </Button>
                  )}
                </Box>
              </Box>
            </Box>

            {/* Tab content */}
            {tabs.map((tab, index) => (
              <div key={index} hidden={this.state.activeTab !== index} style={tab.style}>
                {tab.component}
              </div>
            ))}
          </Grid>
        </Grid>
        {user.isAQS && (
          <ConfigurationLogsModal
            open={this.state.logsModalOpen}
            closeLogsModal={() => this.setState({ logsModalOpen: false })}
            data={this.props.messagesLogs}
            timeZone={this.props.timeZone}
          />
        )}
        {this.state.popupAlert && <PopupAlert {...this.state.popupAlert} />}
      </main>
    );
  }
}

G5DevicesTabsView.contextTypes = {
  t: PropTypes.func.isRequired,
};

G5DevicesTabsView.propTypes = {
  // copy props from MainPage propTypes but not hideHeader
  deviceList: PropTypes.array.isRequired,
  fetchDevices: PropTypes.func.isRequired,
  fetchProjectDefaultConfiguration: PropTypes.func.isRequired,
  fwVersions: PropTypes.array.isRequired,
  fwUltraVersions: PropTypes.array.isRequired,
  fwUltraModemVersions: PropTypes.array.isRequired,
  handleProjectChange: PropTypes.func.isRequired,
  options: PropTypes.array.isRequired,
  projectsList: PropTypes.object.isRequired,
  selectDevice: PropTypes.func.isRequired,
  selectMultipleDevices: PropTypes.func,
  selectedProject: PropTypes.string.isRequired,
  selection: PropTypes.array.isRequired,
  sendActionCommand: PropTypes.func,
  timeZone: PropTypes.string.isRequired,
  updateConfig: PropTypes.func.isRequired,
  user: PropTypes.object.isRequired,
  messagesLogs: PropTypes.array,
};

// reuse the same redux mappings as MainPage
const mapStateToProps = (state, props) => {
  const { user, projectsList, selectedProject, optionList } = state.leaksList;
  const timeZone = state.local.timeZone;
  const {
    deviceList,
    devicesForDisplay,
    sort,
    bitsInfo,
    selection,
    fwVersions,
    fwUltraVersions,
    fwUltraModemVersions,
    fwModemVersions,
    messagesLogs,
  } = state.devcieConfigurations;
  const { options } = optionList;

  return {
    ...props,
    timeZone,
    user,
    deviceList,
    devicesForDisplay,
    sort,
    bitsInfo,
    selection,
    projectsList,
    selectedProject,
    options,
    fwVersions,
    fwUltraVersions,
    fwUltraModemVersions,
    fwModemVersions,
    messagesLogs,
  };
};

function mapDispatchToProps(dispatch, props) {
  return {
    fetchProjectDefaultConfiguration: () => dispatch(actionsDevices.fetchProjectDefaultConfiguration()),
    fetchDevices: () => {
      dispatch(actionsDevices.fetchDevices());
      dispatch(actionsDevices.fetchFwVersions());
      dispatch(actionsDevices.fetchFwUltraVersions());
      dispatch(actionsDevices.fetchFwUltraModemVersions());
      dispatch(actionsDevices.fetchFwModemVersions());
      dispatch(actionsSensors.fetchG5BitInfo());
    },
    selectDevice: (selection, mode) => dispatch(actionsDevices.selectDevices(selection)),
    selectMultipleDevices: (devices) => dispatch(actionsDevices.selectDevices(devices)),
    updateConfig: (values, type, notifyFunc) =>
      dispatch(devicesActions.setDeviceConfiguration(type, values)).then((data) => {
        if (data.err) notifyFunc(JSON.stringify(data.err), 'error');
        else notifyFunc(`${type} message was sent successfully`, 'success');
      }),
    updateDefaultConfig: (values, type, notifyFunc) =>
      dispatch(devicesActions.setProjectDefaultConfiguration(type, values)).then((data) => {
        if (data.err) notifyFunc(JSON.stringify(data.err), 'error');
        else notifyFunc(`${type} defaults was set successfully`, 'success');
      }),
    sendActionCommand: (action) => dispatch(actionsDevices.setActionCommand(action)),
    handleProjectChange: (nextProject) => dispatch(actionsProjects.selectProjectAction(nextProject)),
    searchDevice: (value, type) =>
      new Promise((resolve, reject) => {
        dispatch(
          actionsSearch.searchItem(value, type, 'g5Devices', (featureIndex, serverResult) => {
            if (featureIndex != null) resolve();
            else if (serverResult == null || serverResult.status == false)
              reject(new Error('search failed.'));
            else resolve(serverResult);
          })
        );
      }),
  };
}

export default connect(mapStateToProps, mapDispatchToProps)(G5DevicesTabsView);
